import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useT } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Play, CheckCircle2, Clock, AlertCircle, Workflow } from "lucide-react";

function timeShort(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" }); } catch { return ""; }
}

function Row({ item, t }) {
  const auto = item.mode === "auto" && item.status === "done";
  const err = item.status === "error";
  const Icon = err ? AlertCircle : auto ? CheckCircle2 : Clock;
  const tone = err ? "text-rose-600" : auto ? "text-primary" : "text-amber-600";
  return (
    <div className="flex items-start gap-3 rounded-xl border border-border bg-card p-3" data-testid={`aipa-activity-${item.id}`}>
      <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${tone}`} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-sm font-medium text-foreground">{item.title}</p>
          <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${auto ? "bg-primary/10 text-primary" : "bg-amber-100 text-amber-700"}`}>
            {auto ? t("aipa.mode_auto") : t("aipa.mode_pending")}
          </span>
        </div>
        {item.detail && <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{item.detail}</p>}
        <p className="mt-1 text-[11px] text-muted-foreground/70">{timeShort(item.created_at)}</p>
      </div>
    </div>
  );
}

export default function Automation() {
  const t = useT();
  const [rows, setRows] = useState(null);
  const [running, setRunning] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/coach/assistant/workflows/activity"); setRows(data.activity || []); }
    catch { setRows([]); }
  };
  useEffect(() => { load(); }, []);

  const run = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/coach/assistant/workflows/run");
      const tot = data?.totals || { auto: 0, pending: 0 };
      toast.success(t("aipa.run_done", { auto: tot.auto, pending: tot.pending }));
      await load();
    } catch (err) { toast.error(apiError(err)); } finally { setRunning(false); }
  };

  return (
    <div className="space-y-4" data-testid="aipa-automation">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="font-display text-xl text-foreground">{t("aipa.automation_title")}</h2>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">{t("aipa.automation_desc")}</p>
        </div>
        <Button className="gap-1.5 rounded-full" disabled={running} onClick={run} data-testid="aipa-run-now">
          <Play className="h-4 w-4" /> {running ? t("aipa.running") : t("aipa.run_now")}
        </Button>
      </div>

      <h3 className="pt-2 text-sm font-semibold text-foreground">{t("aipa.activity_title")}</h3>
      {rows === null ? (
        <Skeleton className="h-40 rounded-2xl" />
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center" data-testid="aipa-activity-empty">
          <Workflow className="mx-auto h-8 w-8 text-primary" />
          <p className="mt-3 text-sm text-muted-foreground">{t("aipa.activity_empty")}</p>
        </div>
      ) : (
        <div className="space-y-2">{rows.map((it) => <Row key={it.id} item={it} t={t} />)}</div>
      )}
    </div>
  );
}
